import { useState, useEffect, useCallback } from 'react';
import { Calculator, Loader2 } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useToast } from '../components/ui/Toast';
import { lohnBuchungsbelegApi } from '../services/lohnBuchungsbelegApi';
import type { RunSummary, RunDetail } from '../types/lohnBuchungsbeleg';
import { BelegTab } from '../components/lohn-buchungsbeleg/BelegTab';
import { JournalTab } from '../components/lohn-buchungsbeleg/JournalTab';
import { VorlageTab, type PrefillLine } from '../components/lohn-buchungsbeleg/VorlageTab';
import { BcExportTab } from '../components/lohn-buchungsbeleg/BcExportTab';

type Tab = 'zuordnung' | 'buchungsbeleg' | 'vorlage' | 'bc-export';

export function LohnBuchungsbelegPage() {
    const { canView, canEdit, isLoading } = usePagePermissions('/lohn-buchungsbeleg');
    const { toast } = useToast();
    const [tab, setTab] = useState<Tab>('zuordnung');

    // Run selection + detail live at the page level so the Zuordnung and Buchungsbeleg
    // tabs share the same computed voucher (the journal is derived from it).
    const [runs, setRuns] = useState<RunSummary[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [detail, setDetail] = useState<RunDetail | null>(null);
    const [loadingRuns, setLoadingRuns] = useState(true);
    const [loadingDetail, setLoadingDetail] = useState(false);

    // Whether the Buchungsbeleg (journal) tab has been generated + is visible.
    const [journalGenerated, setJournalGenerated] = useState(false);

    // Feature 2: a template line prefilled from an unmatched Lohnart, opened in the Vorlage tab.
    const [prefillLine, setPrefillLine] = useState<PrefillLine | null>(null);

    const loadRuns = useCallback(async (selectId?: number) => {
        setLoadingRuns(true);
        try {
            const list = await lohnBuchungsbelegApi.listRuns();
            setRuns(list);
            setSelectedId(prev => selectId ?? prev ?? (list[0]?.id ?? null));
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Läufe konnten nicht geladen werden', 'error');
        } finally {
            setLoadingRuns(false);
        }
    }, [toast]);

    const loadDetail = useCallback(async (id: number) => {
        setLoadingDetail(true);
        try {
            setDetail(await lohnBuchungsbelegApi.getRun(id));
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Beleg konnte nicht geladen werden', 'error');
            setDetail(null);
        } finally {
            setLoadingDetail(false);
        }
    }, [toast]);

    useEffect(() => { if (canView) void loadRuns(); }, [canView, loadRuns]);
    useEffect(() => { if (selectedId) void loadDetail(selectedId); }, [selectedId, loadDetail]);
    // Switching runs invalidates a previously generated journal view.
    useEffect(() => { setJournalGenerated(false); }, [selectedId]);

    // Feature 2: prefill the Vorlage tab and switch to it.
    const startPrefillLine = useCallback((prefill: PrefillLine) => {
        setPrefillLine(prefill);
        setTab('vorlage');
    }, []);

    // After a template line is saved from a prefill, recompute the current voucher.
    const onLineSaved = useCallback(() => {
        setPrefillLine(null);
        if (selectedId) void loadDetail(selectedId);
    }, [selectedId, loadDetail]);

    if (isLoading) {
        return <div className="flex h-64 items-center justify-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;
    }
    if (!canView) {
        return (
            <div className="mx-auto max-w-md py-20 text-center">
                <h2 className="text-lg font-semibold text-slate-700">Kein Zugriff</h2>
                <p className="mt-2 text-sm text-slate-500">Du hast keine Berechtigung für den Lohnbuchungsbeleg. Bitte einen Admin, den Zugriff in den Einstellungen freizugeben.</p>
            </div>
        );
    }

    const tabs: Array<[Tab, string, boolean]> = [
        ['zuordnung', 'Zuordnung', true],
        ['buchungsbeleg', 'Buchungsbeleg', journalGenerated && !!detail],
        ['vorlage', 'Vorlage / Einstellungen', true],
        ['bc-export', 'BC-Export', true],
    ];

    return (
        <div className="mx-auto max-w-full px-4 py-5">
            <div className="mb-4 flex items-center gap-2">
                <Calculator className="h-6 w-6 text-amber-600" />
                <div>
                    <h1 className="text-xl font-semibold text-slate-800">Lohnbuchungsbeleg Schweiz</h1>
                    <p className="mt-0.5 text-xs text-slate-500">Rohdaten hochladen · Zuordnung prüfen · Buchungsbeleg generieren · als Excel/PDF exportieren</p>
                </div>
            </div>

            <div className="mb-4 flex gap-1 border-b border-slate-200">
                {tabs.filter(([, , visible]) => visible).map(([key, label]) => (
                    <button
                        key={key}
                        onClick={() => setTab(key)}
                        className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${tab === key ? 'border-amber-500 text-amber-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    >
                        {label}
                    </button>
                ))}
            </div>

            {tab === 'zuordnung' && (
                <BelegTab
                    canEdit={canEdit}
                    runs={runs}
                    selectedId={selectedId}
                    detail={detail}
                    loadingRuns={loadingRuns}
                    loadingDetail={loadingDetail}
                    onSelectRun={setSelectedId}
                    onReloadRuns={loadRuns}
                    onReloadDetail={loadDetail}
                    onClearDetail={() => { setDetail(null); setSelectedId(null); }}
                    onGenerateJournal={() => { setJournalGenerated(true); setTab('buchungsbeleg'); }}
                    onCreateTemplateLine={startPrefillLine}
                />
            )}
            {tab === 'buchungsbeleg' && detail && (
                <JournalTab detail={detail} />
            )}
            {tab === 'vorlage' && (
                <VorlageTab canEdit={canEdit} prefillLine={prefillLine} onPrefillSaved={onLineSaved} />
            )}
            {tab === 'bc-export' && (
                <BcExportTab detail={detail} canEdit={canEdit} />
            )}
        </div>
    );
}

export default LohnBuchungsbelegPage;
